import { useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Loading from '../Loading'
import axiosInstance from '../../utils/axiosInstance'

// UUID regex pattern
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const isUUID = (value: string): boolean => {
  return UUID_PATTERN.test(value)
}

const isDisplayId = (value: string): boolean => {
  return /^\d+$/.test(value)
}

interface OrderRedirectProps {
  children: React.ReactNode
}

const OrderRedirect: React.FC<OrderRedirectProps> = ({ children }) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const handleRedirect = async () => {
      if (!id) return

      // If it's already a UUID, no redirect needed
      if (isUUID(id)) {
        return
      }

      // If it's a display ID, resolve to UUID and redirect
      if (isDisplayId(id)) {
        try {
          const response = await axiosInstance.get(`/orders/by-display-id/${id}`)
          const { id: actualId } = response.data

          // Construct the new URL with the actual UUID
          const currentPath = location.pathname
          const newPath = currentPath.replace(`/${id}`, `/${actualId}`)

          // Replace the current URL with the canonical UUID URL
          navigate(newPath, { replace: true })
        } catch (error) {
          console.error('Error resolving display ID:', error)
          // If the display ID is not found, we could redirect to a 404 page
          // For now, we'll let the existing error handling in OrderTree take care of it
        }
      }

      // If it's neither UUID nor display ID, let the existing error handling deal with it
    }

    handleRedirect()
  }, [id, navigate, location.pathname])

  // Show loading while redirecting from display ID to UUID
  if (id && isDisplayId(id)) {
    return <Loading />
  }

  // Render children for UUID or other cases
  return <>{children}</>
}

export default OrderRedirect
